import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NewOfferDialog } from "@/components/offer/NewOfferDialog";
import BookMeetingFromRecord from "@/components/salgsmuligheter/BookMeetingFromRecord";
import NewFollowUpDialog from "@/components/salgsmuligheter/NewFollowUpDialog";
import CustomerEditDialog from "@/components/customers/CustomerEditDialog";
import LogActivityDialog from "@/components/customers/LogActivityDialog";
import { useCanBookMeetings } from "@/hooks/use-can-book-meetings";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { CustomerDetail } from "@/hooks/use-customer-detail";

interface CustomerHeroActionsProps {
  customer: CustomerDetail;
  onSendEmail: (contactId: string | null) => void;
}

/**
 * Knapperaden i heroen (wireframe): Nytt tilbud, Logg kontakt, Ny handling,
 * Book møte og Rediger. Hver knapp åpner dialogen som ellers lå spredt på
 * seksjonene, så kundekortet har ett sted å handle fra.
 */
export default function CustomerHeroActions({ customer, onSendEmail }: CustomerHeroActionsProps) {
  const koPipe = useFeatureFlag("feature_crm_ko_pipe");
  const canBook = useCanBookMeetings();
  const [offerOpen, setOfferOpen] = useState(false);
  const [logType, setLogType] = useState<"call" | "note" | null>(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div className="flex flex-wrap gap-2" aria-label="Handlinger for kunden">
      <Button size="sm" onClick={() => setOfferOpen(true)}>
        Nytt tilbud
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            Logg kontakt
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onSendEmail(null)}>Send e-post</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setLogType("call")}>Logg samtale</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setLogType("note")}>Logg notat</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {koPipe && (
        <Button size="sm" variant="outline" onClick={() => setFollowUpOpen(true)}>
          Ny handling
        </Button>
      )}
      {canBook && (
        <Button size="sm" variant="outline" onClick={() => setBookOpen(true)}>
          Book møte
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
        Rediger
      </Button>

      <NewOfferDialog open={offerOpen} onOpenChange={setOfferOpen} initialCustomerId={customer.id} />
      <LogActivityDialog
        open={logType !== null}
        onOpenChange={(o) => !o && setLogType(null)}
        customerId={customer.id}
        type={logType ?? "call"}
      />
      {koPipe && <NewFollowUpDialog open={followUpOpen} onOpenChange={setFollowUpOpen} customerId={customer.id} />}
      {canBook && <BookMeetingFromRecord open={bookOpen} onOpenChange={setBookOpen} customerId={customer.id} name={customer.name} />}
      <CustomerEditDialog open={editOpen} onOpenChange={setEditOpen} customer={customer} />
    </div>
  );
}
